/* File upload component using the backend /api/upload endpoint. */
import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Upload, X, Loader2, FileText, Eye } from "lucide-react";
import { toast } from "sonner";
import { BACKEND_URL } from "@/lib/api";

const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

export function buildFileUrl(id) {
  if (!id) return null;
  return `${BACKEND_URL}/api/files/${id}`;
}

async function compressImageIfNeeded(file) {
  if (!(file instanceof File)) return file;
  if (!file.type.startsWith("image/")) return file;
  if (file.type.includes("gif") || file.type.includes("svg")) return file;

  const bitmap = await createImageBitmap(file);
  const maxDimension = 1920;
  const scale = Math.min(
    1,
    maxDimension / Math.max(bitmap.width, bitmap.height),
  );
  const targetW = Math.max(1, Math.round(bitmap.width * scale));
  const targetH = Math.max(1, Math.round(bitmap.height * scale));

  const canvas = document.createElement("canvas");
  canvas.width = targetW;
  canvas.height = targetH;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    bitmap.close();
    return file;
  }
  ctx.drawImage(bitmap, 0, 0, targetW, targetH);
  bitmap.close();

  const outputType = file.type.includes("png") ? "image/png" : "image/webp";
  const quality = outputType === "image/webp" ? 0.82 : undefined;
  const blob = await new Promise((resolve) => {
    canvas.toBlob((b) => resolve(b), outputType, quality);
  });

  if (!blob || blob.size >= file.size) return file;

  const renamed =
    outputType === "image/webp"
      ? file.name.replace(/\.(jpe?g|png|webp)$/i, ".webp")
      : file.name;
  return new File([blob], renamed, {
    type: outputType,
    lastModified: Date.now(),
  });
}

export function FileUploader({
  value,
  onChange,
  accept = ".pdf,.jpg,.jpeg,.png,.webp",
  label,
  tag = "doc",
  testid,
  enforceMaxSize = true,
  enableCrop = false,
}) {
  const ref = useRef();
  const cropCanvasRef = useRef();
  const [busy, setBusy] = useState(false);
  const [cropState, setCropState] = useState(null);

  useEffect(() => {
    if (!cropState?.bitmap || !cropCanvasRef.current) return;

    const canvas = cropCanvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const bitmap = cropState.bitmap;
    const base = Math.max(
      1,
      Math.min(bitmap.width, bitmap.height) / Math.max(1, cropState.zoom),
    );
    const maxShiftX = Math.max(0, (bitmap.width - base) / 2);
    const maxShiftY = Math.max(0, (bitmap.height - base) / 2);

    const sx = Math.min(
      bitmap.width - base,
      Math.max(
        0,
        bitmap.width / 2 - base / 2 + (cropState.offsetX / 100) * maxShiftX,
      ),
    );
    const sy = Math.min(
      bitmap.height - base,
      Math.max(
        0,
        bitmap.height / 2 - base / 2 + (cropState.offsetY / 100) * maxShiftY,
      ),
    );

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(
      bitmap,
      sx,
      sy,
      base,
      base,
      0,
      0,
      canvas.width,
      canvas.height,
    );
  }, [cropState]);

  const resetFileInput = () => {
    if (ref.current) ref.current.value = "";
  };

  const uploadPreparedFile = async (inputFile, originalFile = null) => {
    const sourceFile = originalFile || inputFile;
    const readyFile = await compressImageIfNeeded(inputFile);

    if (enforceMaxSize && readyFile.size > MAX_UPLOAD_BYTES) {
      toast.error("Max 10MB after compression");
      return;
    }

    const form = new FormData();
    form.append("file", readyFile, readyFile.name);
    const { data } = await api.post(
      `/upload?tag=${encodeURIComponent(tag)}`,
      form,
      {
        headers: { "Content-Type": "multipart/form-data" },
      },
    );

    onChange?.(data);
    if (readyFile !== sourceFile) {
      toast.success(
        `Image compressed ${(sourceFile.size / 1024).toFixed(0)}KB -> ${(readyFile.size / 1024).toFixed(0)}KB`,
      );
    }
    toast.success("Uploaded");
  };

  const closeCropper = () => {
    if (cropState?.bitmap) {
      cropState.bitmap.close();
    }
    setCropState(null);
    resetFileInput();
  };

  const openCropper = async (file) => {
    const bitmap = await createImageBitmap(file);
    setCropState({
      file,
      bitmap,
      zoom: 1,
      offsetX: 0,
      offsetY: 0,
    });
  };

  const applyCropAndUpload = async () => {
    if (!cropState?.bitmap || !cropState?.file) return;

    const bitmap = cropState.bitmap;
    const base = Math.max(
      1,
      Math.min(bitmap.width, bitmap.height) / Math.max(1, cropState.zoom),
    );
    const maxShiftX = Math.max(0, (bitmap.width - base) / 2);
    const maxShiftY = Math.max(0, (bitmap.height - base) / 2);

    const sx = Math.min(
      bitmap.width - base,
      Math.max(
        0,
        bitmap.width / 2 - base / 2 + (cropState.offsetX / 100) * maxShiftX,
      ),
    );
    const sy = Math.min(
      bitmap.height - base,
      Math.max(
        0,
        bitmap.height / 2 - base / 2 + (cropState.offsetY / 100) * maxShiftY,
      ),
    );

    const outCanvas = document.createElement("canvas");
    outCanvas.width = 1080;
    outCanvas.height = 1080;
    const outCtx = outCanvas.getContext("2d");
    if (!outCtx) {
      toast.error("Crop failed");
      return;
    }
    outCtx.drawImage(
      bitmap,
      sx,
      sy,
      base,
      base,
      0,
      0,
      outCanvas.width,
      outCanvas.height,
    );

    const outputType = cropState.file.type.includes("png")
      ? "image/png"
      : "image/webp";
    const blob = await new Promise((resolve) => {
      outCanvas.toBlob((b) => resolve(b), outputType, 0.9);
    });
    if (!blob) {
      toast.error("Crop failed");
      return;
    }

    const fileName =
      outputType === "image/webp"
        ? cropState.file.name.replace(/\.(jpe?g|png|webp)$/i, ".webp")
        : cropState.file.name;

    const croppedFile = new File([blob], fileName, {
      type: outputType,
      lastModified: Date.now(),
    });

    setBusy(true);
    try {
      await uploadPreparedFile(croppedFile, cropState.file);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    }
    setBusy(false);
    closeCropper();
  };

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;

    if (enableCrop && f.type.startsWith("image/")) {
      try {
        await openCropper(f);
      } catch {
        toast.error("Could not open crop tool");
        resetFileInput();
      }
      return;
    }

    setBusy(true);
    try {
      await uploadPreparedFile(f);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    }
    setBusy(false);
    resetFileInput();
  };

  const clear = () => onChange?.(null);

  const fileId = typeof value === "string" ? value : value?.id;
  const isImage =
    typeof value === "object" && !!value?.content_type?.startsWith("image/");
  const previewUrl = buildFileUrl(fileId);
  const fileName =
    typeof value === "object" ? value?.filename : "uploaded-file";
  const fileSizeKb =
    typeof value === "object" ? Math.round((value?.size || 0) / 1024) : null;

  return (
    <div className="w-full" data-testid={testid}>
      {label && (
        <div className="text-[11px] font-semibold text-slate-700 mb-1">
          {label}
        </div>
      )}
      {value ? (
        <div className="relative border border-slate-200 rounded-md p-2 bg-slate-50/50 group hover:bg-white transition">
          <div className="flex items-center gap-2">
            {isImage ? (
              <img
                src={previewUrl}
                alt=""
                className="w-12 h-12 object-cover rounded border border-slate-200"
              />
            ) : (
              <div className="w-12 h-12 rounded bg-rose-50 border border-rose-200 flex items-center justify-center">
                <FileText className="w-5 h-5 text-rose-600" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-xs font-medium text-slate-800 truncate">
                {fileName || "file"}
              </div>
              <div className="text-[10px] text-slate-500">
                {fileSizeKb != null ? `${fileSizeKb} KB` : "Uploaded"}
              </div>
            </div>
            <a
              href={previewUrl}
              target="_blank"
              rel="noreferrer"
              className="p-1 rounded hover:bg-slate-100 text-slate-600"
              data-testid={`${testid}-preview`}
            >
              <Eye className="w-3.5 h-3.5" />
            </a>
            <button
              onClick={clear}
              className="p-1 rounded hover:bg-red-50 text-red-600"
              data-testid={`${testid}-clear`}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => ref.current?.click()}
          disabled={busy}
          data-testid={`${testid}-upload`}
          className="w-full border-2 border-dashed border-slate-300 rounded-md p-3 hover:border-[var(--erp-primary)] hover:bg-slate-50 transition text-center"
        >
          {busy ? (
            <div className="flex items-center justify-center gap-2 text-slate-500 text-xs">
              <Loader2 className="w-4 h-4 animate-spin" /> Uploading…
            </div>
          ) : (
            <div className="flex items-center justify-center gap-2 text-slate-500 text-xs">
              <Upload className="w-4 h-4" />
              <span>Click to upload</span>
              <span className="text-slate-400">· PDF/JPG/PNG</span>
              {enableCrop && (
                <span className="text-slate-400">· crop enabled</span>
              )}
            </div>
          )}
        </button>
      )}
      <input ref={ref} type="file" accept={accept} hidden onChange={upload} />

      {cropState && (
        <div className="fixed inset-0 z-[120] bg-slate-900/70 flex items-center justify-center p-4">
          <div className="w-full max-w-xl rounded-xl bg-white border border-slate-200 shadow-xl p-4">
            <div className="text-sm font-semibold text-slate-800 mb-3">
              Crop Student Photo
            </div>
            <div className="grid grid-cols-1 md:grid-cols-[240px_1fr] gap-4 items-start">
              <canvas
                ref={cropCanvasRef}
                width={240}
                height={240}
                className="w-[240px] h-[240px] rounded-lg border border-slate-300 bg-slate-100"
              />
              <div className="space-y-3">
                <div>
                  <div className="text-[11px] font-semibold text-slate-700 mb-1">
                    Zoom
                  </div>
                  <input
                    type="range"
                    min="1"
                    max="3"
                    step="0.01"
                    value={cropState.zoom}
                    onChange={(e) =>
                      setCropState((s) => ({
                        ...s,
                        zoom: Number(e.target.value),
                      }))
                    }
                    className="w-full"
                  />
                </div>
                <div>
                  <div className="text-[11px] font-semibold text-slate-700 mb-1">
                    Horizontal
                  </div>
                  <input
                    type="range"
                    min="-100"
                    max="100"
                    step="1"
                    value={cropState.offsetX}
                    onChange={(e) =>
                      setCropState((s) => ({
                        ...s,
                        offsetX: Number(e.target.value),
                      }))
                    }
                    className="w-full"
                  />
                </div>
                <div>
                  <div className="text-[11px] font-semibold text-slate-700 mb-1">
                    Vertical
                  </div>
                  <input
                    type="range"
                    min="-100"
                    max="100"
                    step="1"
                    value={cropState.offsetY}
                    onChange={(e) =>
                      setCropState((s) => ({
                        ...s,
                        offsetY: Number(e.target.value),
                      }))
                    }
                    className="w-full"
                  />
                </div>
                <div className="text-[11px] text-slate-500">
                  Tip: center face in preview, then apply crop.
                </div>
              </div>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={closeCropper}
                className="h-9 px-3 rounded-md border border-slate-300 text-sm text-slate-700"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={applyCropAndUpload}
                className="h-9 px-3 rounded-md bg-[var(--erp-primary)] text-white text-sm"
                disabled={busy}
              >
                {busy ? "Uploading..." : "Apply Crop & Upload"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
